import { useEffect, useRef, useState } from 'react';
import { VariationGroupModel, VariationItemModel } from '@src/components/variation-picker/VariationPicker';
import IconWithTooltip from '@src/components/icon-with-tooltip/IconWithTooltip';
import { useOrderInfoContext } from '@src/app/order-info-context';
import QuantitySelector from '../quantity-selector/QuantitySelector';

interface Props {
  group: VariationGroupModel;
  quantityQuestion: string;
  selected: boolean;
  extraClassname?: string;
  quantityRemainingText?: string;
  handleVariationChange: (item: VariationItemModel | undefined) => void;
}

const VariationGroup = ({ group, quantityQuestion, extraClassname, quantityRemainingText, handleVariationChange }: Props) => {
  const {
    groupId,
    productCode,
    quantity,
    variantId,
    setGroupId,
    setProductCode,
    setQuantity,
    setVariantId,
    setBidAllocationProduct,
    selectedItem,
    setSelectedItem,
  } = useOrderInfoContext();
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    if (groupId === group.groupId && selectedIndex >= 0 && selectedIndex < group?.items?.length && group.items[selectedIndex]) {
      const item = group.items[selectedIndex];

      if (item.variantId !== variantId || item.productCode !== productCode) {
        setQuantity(item.minQuantity);
      }

      setProductCode(item.productCode);
      setVariantId(item.variantId);
      setSelectedItem && setSelectedItem(item);
      handleVariationChange(item);
      setBidAllocationProduct && setBidAllocationProduct(item.bidAllocationProduct ?? false);
    } else if (!groupId) {
      setSelectedIndex(-1);
      setSelectedItem && setSelectedItem(undefined);
      handleVariationChange(undefined);
      setBidAllocationProduct && setBidAllocationProduct(false);
    }
  }, [selectedIndex, group, groupId]);

  useEffect(() => {
    if (!!variantId && variantId > 0) {
      const index = group.items?.findIndex((i) => i.variantId == variantId && i.productCode === productCode);
      if (index >= 0 && group.items[index]) {
        setSelectedIndex(index);
      }
    }
  }, []);

  useEffect(() => {
    if (groupId === group.groupId) {
      if (!group?.items?.length) {
        setSelectedIndex(-1);
        return;
      }

      if (!!variantId && variantId > 0) {
        const index = group.items.findIndex((i) => i.variantId === variantId);
        if (index >= 0) {
          setSelectedIndex(index);
          return;
        }
      }

      setSelectedIndex(0);
    } else {
      setSelectedIndex(-1);
    }
  }, [groupId, variantId, group]);

  const handleSelectPrevItem = () => {
    if (!group?.items?.length) {
      setSelectedIndex(-1);
      return;
    }

    const index = selectedIndex > 0 ? selectedIndex - 1 : group.items.length - 1;
    setSelectedIndex(index);
  };

  const handleSelectNextItem = () => {
    if (!group?.items?.length) {
      setSelectedIndex(-1);
      return;
    }

    const index = selectedIndex + 1 < group.items.length ? selectedIndex + 1 : 0;
    setSelectedIndex(index);
  };

  const handleGroupClick = () => {
    if (groupId !== group.groupId) {
      // reset height
      const el = contentRef.current as HTMLDivElement;
      const wrapper = el.closest<HTMLElement>('.fm-o-variation-switcher__swiper');

      wrapper?.style.removeProperty('--max-item-height');
    }

    setGroupId(group.groupId);
  };

  // process height item include slug
  const slugRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);

  const setItemHeight = () => {
    if (contentRef.current) {
      const el = contentRef.current as HTMLDivElement;
      const wrapper = el.closest<HTMLElement>('.fm-o-variation-switcher__swiper');
      const currentItemHeight = parseInt(wrapper?.style.getPropertyValue('--max-item-height') || '0');

      // reset height
      wrapper?.style.removeProperty('--max-item-height');

      const slugHeight = slugRef.current?.offsetHeight ?? 0;
      const contentHeight = el.offsetHeight;

      if (currentItemHeight < slugHeight + contentHeight) {
        wrapper?.style.setProperty('--max-item-height', `${slugHeight + contentHeight}px`);
      } else {
        wrapper?.style.setProperty('--max-item-height', `${currentItemHeight}px`);
      }

      groupRef.current?.style.setProperty('--slug-height', `${slugHeight}px`);
    }
  };

  useEffect(() => {
    setItemHeight();
  }, [selectedItem]);

  if (groupId != group.groupId || !selectedItem) {
    return (
      <div
        ref={groupRef}
        className={`fm-o-variation-switcher__group ${!group.tooltip ? 'no-tooltip' : ''} ${extraClassname ?? ''}`}
        onClick={handleGroupClick}
      >
        {group.slug && (
          <div ref={slugRef} className="fm-o-variation-switcher__slug">
            {group.slug}
          </div>
        )}

        <div className="fm-o-variation-switcher__group-container" ref={contentRef}>
          <div className="fm-o-variation-switcher__image-wrapper">
            <img className="fm-o-variation-switcher__image" src={group.src} alt={group.name} onLoad={setItemHeight} />
          </div>

          <div className="fm-o-variation-switcher__group-info">
            <div className="fm-o-variation-switcher__group-name-n-tooltip">
              <h5 className="fm-o-variation-switcher__group-name">{group.name}</h5>

              {group.tooltip && (
                <div className="fm-o-variation-switcher__group-tooltip">
                  <IconWithTooltip text={group.tooltip} />
                </div>
              )}
            </div>

            <div className="fm-o-variation-switcher__group-desc">{group.text}</div>
          </div>
        </div>
      </div>
    );
  }

  // set max quantity
  let maxQuantity = selectedItem.maxQuantity;

  if (typeof selectedItem.maxQuantity === 'number' && typeof selectedItem.quantityRemaining === 'number') {
    maxQuantity = Math.min(selectedItem.maxQuantity, selectedItem.quantityRemaining);
  }

  return (
    <div ref={groupRef} className={'fm-o-variation-switcher__group fm-o-variation-switcher__group--selected'} onClick={handleGroupClick}>
      {group.slug && (
        <div ref={slugRef} className="fm-o-variation-switcher__slug">
          {group.slug}
        </div>
      )}

      <div ref={contentRef} className="fm-o-variation-switcher__group-container fm-o-variation-switcher__group-container--selected">
        <div className="fm-o-variation-switcher__item-name">{group.items.length > 1 && <h5>{selectedItem.name}</h5>}</div>

        <div
          className={
            'fm-o-variation-switcher__image-wrapper' +
            (group.items.length > 1 ? ' fm-o-variation-switcher__image-wrapper--grow' : ' fm-o-variation-switcher__image-wrapper--single')
          }
        >
          {group.items.length > 1 && (
            <button className="tfm" onClick={handleSelectPrevItem}>
              <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="17" cy="17" r="17" transform="rotate(180 17 17)" fill="#1B1D2B" />
                <path
                  d="M19.1796 22.2705L14.4243 17.5151L19.1796 12.7598"
                  stroke="white"
                  stroke-width="1.54545"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          )}

          <img className="fm-o-variation-switcher__image" src={selectedItem.src} alt={selectedItem.name} onLoad={setItemHeight} />

          {group.items.length === 1 && <h5 className="fm-o-variation-switcher__item-name--single">{selectedItem.name}</h5>}

          {group.items.length > 1 && (
            <button className="tfm" onClick={handleSelectNextItem}>
              <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle r="17" transform="matrix(1 8.74228e-08 8.74228e-08 -1 17 17)" fill="#1B1D2B" />
                <path
                  d="M14.8204 22.27L19.5757 17.5146L14.8204 12.7593"
                  stroke="white"
                  stroke-width="1.59091"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {typeof selectedItem.quantityRemaining === 'number' &&
          selectedItem.quantityRemaining >= 0 &&
          selectedItem.quantityRemaining < 10000 &&
          quantityRemainingText && (
            <div className="fm-o-variation-switcher__qty-remain-wrap">
              <div className="fm-o-variation-switcher__qty-remain-title">{quantityRemainingText || ''}</div>

              <div className="fm-o-variation-switcher__qty-remain-value">{selectedItem.quantityRemaining}</div>
            </div>
          )}

        <div
          className={`fm-o-variation-switcher__quantity-selector ${
            group.items.length === 1 ? 'fm-o-variation-switcher__quantity-selector--single' : ''
          }`}
        >
          <div>{quantityQuestion}</div>

          <QuantitySelector
            key={selectedItem.variantId}
            instantUpdate={true}
            minQuantity={selectedItem.minQuantity}
            maxQuantity={maxQuantity}
            quantity={typeof selectedItem.quantityRemaining === 'number' && selectedItem.quantityRemaining === 0 ? 0 : quantity}
            onSetQuantity={setQuantity}
            batchQuantity={selectedItem.batchQuantity ?? 1}
          />
        </div>
      </div>

      <div className="fm-o-variation-switcher__item-note">{selectedItem.note}</div>
    </div>
  );
};
export default VariationGroup;
